import { useState } from "react";
import { createFileRoute, Link, useNavigate, useRouter } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useSession, sessionKey } from "@/lib/session";
import type { User } from "@/lib/types";
import { Button } from "@/ui/Button";
import { Field, Input } from "@/ui/Field";
import { AuthCard, FormError } from "@/features/auth/AuthCard";
import { isSafeRedirect } from "@/lib/redirect";

export const Route = createFileRoute("/_public/two-factor")({
  component: TwoFactorScreen,
  // Search params are declared optional so that a plain <Link to="/sign-in">
  // elsewhere in the app does not have to spell out an empty redirect.
  // An authentication screen is never a place to come back to, whoever put it
  // in the address — this guard, a stale bookmark, or a link someone was sent.
  // Refused at parse time, so no screen can act on one. See lib/redirect.ts.
  validateSearch: (search: Record<string, unknown>): { redirect?: string } =>
    typeof search.redirect === "string" && isSafeRedirect(search.redirect)
      ? { redirect: search.redirect }
      : {},
});

/**
 * The second-factor challenge.
 *
 * One field accepts both a six-digit code and a recovery code: asking someone
 * who has lost their phone to first find the right tab is a poor moment to add
 * a decision.
 */
function TwoFactorScreen() {
  const navigate = useNavigate();
  const router = useRouter();
  const { refresh } = useSession();
  const queryClient = useQueryClient();
  const { redirect } = Route.useSearch();
  const [code, setCode] = useState("");

  const verify = useMutation({
    mutationFn: () =>
      api.post<{ user: User; mfa_pending?: boolean }>("/auth/mfa/verify", { code: code.trim() }),
    onSuccess: async (result) => {
      // The answer is already in hand, so it goes straight into the cache
      // rather than being asked for again. A refetch here is a second question
      // whose answer can arrive late or stale; this one cannot.
      queryClient.setQueryData(sessionKey, { user: result.user, mfa_pending: false });
      await refresh();
      await router.invalidate();
      await navigate({ to: redirect ?? "/" });
    },
  });

  return (
    <AuthCard
      title="Two-factor authentication"
      subtitle="Enter the code from your authenticator app, or one of your recovery codes."
      footer={
        <Link to="/sign-in" className="font-medium text-[var(--primary)] hover:underline">
          Use a different account
        </Link>
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          verify.mutate();
        }}
      >
        <Field label="Authentication code" required>
          {({ id, describedBy }) => (
            <Input
              id={id}
              inputMode="text"
              autoComplete="one-time-code"
              autoFocus
              required
              placeholder="123456"
              className="mono tracking-[0.2em]"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              aria-describedby={describedBy}
            />
          )}
        </Field>

        {verify.error ? <FormError error={verify.error} /> : null}

        <Button type="submit" tone="primary" wide busy={verify.isPending}>
          Verify
        </Button>
      </form>
    </AuthCard>
  );
}
